import { createBrowserRouter, Navigate } from 'react-router-dom'
import { Layout } from './components/Layout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { RouteErrorBoundary } from './components/RouteErrorBoundary'
import { CalibrationDashboard, CalibrationList } from './modules/calibrations'
import { CycleTasks } from './modules/cycles/CycleTasks'
import { CycleExecution } from './modules/cycles/cycle-execution/CycleExecution'
import { HoldStep } from './modules/cycles/cycle-execution/HoldStep'
import { ReviewPreviewPage } from './modules/cycles/review-preview/ReviewPreviewPage'
import { DashboardPage } from './modules/dashboard'
import { CycleRedirector } from './modules/dashboard/Redirector'
import { AggregationPortalPage } from './modules/dealteam/aggregation/AggregationPortalPage'
import { DealDetailsPage } from './modules/dealteam/aggregation/DealDetailsPage'
import { ParticipantDealReportPage } from './modules/dealteam/aggregation/ParticipantDealReportPage'
import {
	EnpsExternalSurveyPage,
	EnpsPageContainer,
	EnpsResponsePage,
	EnpsSurveyPage,
	ManageEnpsPage,
} from './modules/enps'
import {
	EnpsConfigurationTab,
	EnpsEmailTemplateTab,
	EnpsGeneralTab,
	EnpsLaunchTab,
	EnpsPopulationTab,
	EnpsResultsTab,
	EnpsScheduleTab,
} from './modules/enps/tabs'
import { ReviewerSelectionPage } from './modules/feedback/ReviewerSelectionPage'
import { HrReviewFlagsPage } from './modules/hr-review-flags/HrReviewFlagsPage'
import { CallbackPage, LoginPage, LogoutPage, SsoLaunchPage } from './modules/login'
import {
	LoopExecution,
	LoopResultsDetailPage,
	LoopResultsPage,
	LoopsPageContainer,
	ManageLoopPage,
} from './modules/loops'
import {
	LoopConfigurationTab,
	LoopEmailTemplateTab,
	LoopGeneralTab,
	LoopLaunchTab,
	LoopParticipantsTab,
	LoopResultsTab,
	LoopScheduleTab,
} from './modules/loops/tabs'
import { ManagePecPage, PecPageContainer, PecSurveyPage } from './modules/pec'
import {
	PecAnnouncementEmailTab,
	PecConfigurationTab,
	PecGeneralTab,
	PecLaunchTab,
	PecPopulationTab,
	PecReminderEmailTab,
	PecResultsTab,
} from './modules/pec/tabs'
import { ReportsPage } from './modules/reports'
import { DetailedStatusReports } from './modules/reports/DetailedStatusReports'
import { LeadershipDashboardPage } from './modules/reports/leadership-dashboard'
import { ManagePdfsPage } from './modules/reports/ManagePdfsPage'
import { ManagerPdfsPage } from './modules/reports/ManagerPdfsPage'
import { ReportsOverview } from './modules/reports/ReportsOverview'
import { ReportsSidebar } from './modules/reports/ReportsSidebar'
import { ReportBuilderPage } from './modules/reports/report-builder'
import SearchEvaluationPage from './modules/search-evaluation'
import { CycleExecutionViewer } from './modules/search-evaluation-viewer/CycleExecutionView'
import { SearchEvaluationAsViewerPage } from './modules/search-evaluation-viewer/SearchEvaluationAsViewer'
import { TaskAccessPage } from './modules/tasks/TaskAccessPage'
import { UserFormPage, UsersPage } from './modules/users'
import { TestSentryPage } from './pages/TestSentryPage'

// Loop child routes
const loopChildRoutes = [
	{ index: true, element: <Navigate to='general' replace /> },
	{ path: 'general', element: <LoopGeneralTab /> },
	{ path: 'participants', element: <LoopParticipantsTab /> },
	{ path: 'configuration', element: <LoopConfigurationTab /> },
	{ path: 'schedule', element: <LoopScheduleTab /> },
	{ path: 'email-template', element: <LoopEmailTemplateTab /> },
	{ path: 'launch', element: <LoopLaunchTab /> },
	{ path: 'results', element: <LoopResultsTab /> },
]

// eNPS child routes
const enpsChildRoutes = [
	{ index: true, element: <Navigate to='general' replace /> },
	{ path: 'general', element: <EnpsGeneralTab /> },
	{ path: 'configuration', element: <EnpsConfigurationTab /> },
	{ path: 'schedule', element: <EnpsScheduleTab /> },
	{ path: 'population', element: <EnpsPopulationTab /> },
	{ path: 'email-template', element: <EnpsEmailTemplateTab /> },
	{ path: 'launch', element: <EnpsLaunchTab /> },
	{ path: 'results', element: <EnpsResultsTab /> },
]

// PEC child routes
const pecChildRoutes = [
	{ index: true, element: <Navigate to='general' replace /> },
	{ path: 'general', element: <PecGeneralTab /> },
	{ path: 'population', element: <PecPopulationTab /> },
	{ path: 'configuration', element: <PecConfigurationTab /> },
	{ path: 'announcement-email', element: <PecAnnouncementEmailTab /> },
	{ path: 'reminder-email', element: <PecReminderEmailTab /> },
	{ path: 'launch', element: <PecLaunchTab /> },
	{ path: 'results', element: <PecResultsTab /> },
]

export const router = createBrowserRouter([
	{
		// Catch route load errors and unmatched paths (404) so users see our friendly page
		// instead of React Router's default "Unexpected Application Error!" stack-trace screen.
		errorElement: <RouteErrorBoundary />,
		children: [
			{
				path: '/auth/callback',
				element: <CallbackPage />,
				handle: { title: 'Logging in...' },
			},
			{
				path: '/auth/login',
				element: <LoginPage />,
				handle: { title: 'Login' },
			},
			{
				path: '/auth/launch',
				element: <SsoLaunchPage />,
				handle: { title: 'Signing in...' },
			},
			{
				path: '/auth/logout',
				element: <LogoutPage />,
				handle: { title: 'Logging out...' },
			},
			{
				path: '/tasks/access/:token',
				element: <TaskAccessPage />,
				handle: { title: 'Task Access' },
			},
			{
				path: '/enps/response/:executionId',
				element: <EnpsResponsePage />,
				handle: { title: 'NPS Survey' },
			},
			{
				path: '/enps/external/:token',
				element: <EnpsExternalSurveyPage />,
				handle: { title: 'NPS Survey' },
			},
			{
				element: <ProtectedRoute />,
				children: [
					{
						path: '/',
						element: <Layout />,
						children: [
							{
								index: true,
								element: <Navigate to='/dashboard' replace />,
							},
							{
								path: 'dashboard',
								element: <DashboardPage />,
								handle: { title: 'Dashboard' },
							},
							{
								path: 'test-sentry',
								element: <TestSentryPage />,
								handle: { title: 'Test Sentry' },
							},
							{
								path: 'reports',
								element: <ReportsSidebar />,
								children: [
									{
										index: true,
										element: <Navigate to='/reports/detailed-data-reports' replace />,
									},
									{
										path: 'detailed-data-reports',
										element: <ReportsPage />,
									},
									{
										path: 'status-overview',
										element: <ReportsOverview />,
									},
									{
										path: 'employee-status-table',
										element: <DetailedStatusReports />,
									},
									{
										path: 'manage-pdfs',
										element: <ManagePdfsPage />,
									},
									{
										path: 'manager-pdfs',
										element: <ManagerPdfsPage />,
									},
									{
										path: 'report-builder',
										element: <ReportBuilderPage />,
									},
									{
										path: 'leadership-dashboard',
										element: <LeadershipDashboardPage />,
									},
								],
								handle: { title: 'Reports' },
							},
							{
								path: 'dashboard/:cycleId/tasks',
								element: <CycleTasks />,
								handle: {
									title: 'Cycle Tasks',
								},
							},
							{
								path: 'hr-review-flags',
								element: <HrReviewFlagsPage />,
								handle: { title: 'HR Review Flags' },
							},
							{
								path: 'cycle/:cycleId/tasks/:taskId/cycle-execution',
								element: <CycleExecution />,
								handle: {
									title: 'Cycle Execution',
								},
							},
							{
								path: 'cycle/:cycleId/tasks/:taskId/edit-reviewers-list',
								element: <ReviewerSelectionPage isManagerView={false} />,
								handle: { title: 'Edit Reviewers' },
							},
							{
								path: 'cycle/:cycleId/tasks/:taskId/approve-reviewers-list',
								element: <ReviewerSelectionPage isManagerView={true} />,
								handle: { title: 'Approve Reviewers' },
							},
							{
								path: 'cycle/:cycleId/tasks/:taskId/hold',
								element: <HoldStep />,
								handle: { title: 'Hold' },
							},
							{
								path: 'loops',
								element: <LoopsPageContainer />,
								handle: {
									title: 'Loops',
								},
							},
							{
								path: 'loops/create',
								element: <ManageLoopPage />,
								children: loopChildRoutes,
								handle: {
									title: 'Create Loop',
								},
							},
							{
								path: 'loops/:loopId',
								element: <ManageLoopPage />,
								children: loopChildRoutes,
								handle: {
									title: 'Manage Loop',
								},
							},
							{
								path: 'loops/:loopId/results',
								element: <LoopResultsPage />,
								handle: {
									title: 'Loop Results',
								},
							},
							{
								path: 'loops/:loopId/results/execution/:executionId',
								element: <LoopResultsDetailPage />,
								handle: {
									title: 'Loop Execution Results',
								},
							},
							{
								path: 'loops/:loopId/tasks/:taskId/execution',
								element: <LoopExecution />,
								handle: {
									title: 'Loop Response',
								},
							},
							{
								path: 'pec',
								element: <PecPageContainer />,
								handle: {
									title: 'PEC Cycles',
								},
							},
							{
								path: 'pec/create',
								element: <ManagePecPage />,
								children: pecChildRoutes,
								handle: {
									title: 'Create PEC Cycle',
								},
							},
							{
								path: 'pec/:pecId',
								element: <ManagePecPage />,
								children: pecChildRoutes,
								handle: {
									title: 'Manage PEC Cycle',
								},
							},
							{
								path: 'pec/survey/:pecCycleId',
								element: <PecSurveyPage />,
								handle: {
									title: 'PEC Survey',
								},
							},
							{
								path: 'enps',
								element: <EnpsPageContainer />,
								handle: {
									title: 'NPS Surveys',
								},
							},
							{
								path: 'enps/create',
								element: <ManageEnpsPage />,
								children: enpsChildRoutes,
								handle: {
									title: 'Create NPS',
								},
							},
							{
								path: 'enps/:enpsId',
								element: <ManageEnpsPage />,
								children: enpsChildRoutes,
								handle: {
									title: 'Manage NPS',
								},
							},
							{
								path: 'enps/respond/:executionId',
								element: <EnpsSurveyPage />,
								handle: {
									title: 'NPS Survey',
								},
							},
							{
								path: '/dashboard/feedback',
								element: <div>Feedback</div>,
								handle: { title: 'Feedback' },
							},
							{
								//TODO: update this to use the correct path in the future
								path: 'dashboard/selection',
								element: <ReviewerSelectionPage />,
								handle: { title: 'Reviewer Selection' },
							},
							{
								path: 'dashboard/cycle/:cycleId',
								element: <CycleRedirector />,
								handle: { title: 'Loading...' },
							},
							{
								path: 'evaluations/:cycleId',
								element: <SearchEvaluationPage />,
								handle: { title: 'Evaluations' },
							},
							{
								path: 'calibrations',
								element: <CalibrationList />,
								handle: { title: 'Calibration Sessions' },
							},
							{
								path: 'calibration/:cycleId/:sessionId',
								element: <CalibrationDashboard />,
								handle: { title: 'Calibration' },
							},
							{
								path: 'users',
								element: <UsersPage />,
								handle: { title: 'Users' },
							},
							{
								path: 'users/create',
								element: <UserFormPage />,
								handle: { title: 'Add User' },
							},
							{
								path: 'users/:userId',
								element: <UserFormPage />,
								handle: { title: 'Edit User' },
							},
							{
								path: 'dashboard/evaluations-view/:cycleId',
								element: <SearchEvaluationAsViewerPage />,
								handle: { title: 'Evaluations View' },
							},
							{
								path: 'cycle/:cycleId/cycle-view/:evaluationId',
								element: <CycleExecutionViewer />,
								handle: { title: 'Cycle Execution' },
							},
							{
								path: 'cycle/:cycleId/evaluations/:evaluationId/review-preview',
								element: <ReviewPreviewPage />,
								handle: { title: 'Review Preview' },
							},
							{
								path: 'dealteam',
								element: <AggregationPortalPage />,
								handle: { title: 'DealTeam' },
							},
							{
								path: 'dealteam/deals/:cycleId',
								element: <DealDetailsPage />,
								handle: { title: 'Deal Details' },
							},
							{
								path: 'dealteam/deals/:cycleId/participants/:userId/report',
								element: <ParticipantDealReportPage />,
								handle: { title: 'Participant Report' },
							},
						],
					},
				],
			},
		],
	},
])
